/* global atob Blob */
import { createElement, readBlob } from 'app/utils/env';
import { loadPdf, PDF } from './PDF';

export interface IFirstPagePreview {
  width: number,
  height: number,
  blob: Blob,
}

const generatePdfPreview = async (file: File): Promise<IFirstPagePreview> => {
  // Things that need cleanup in the `finally` block
  let pdf: PDF | undefined;
  let canvas: HTMLCanvasElement | undefined;

  try {
    const arrayBuffer: ArrayBuffer = await readBlob(file);
    const data: Uint8Array = new Uint8Array(arrayBuffer);
    pdf = await loadPdf({ data, scale: 0.5, start: 1, end: 1 });
    const size = pdf.getPageSize(1);

    if (!size) {
      throw new Error(`Couldn't get size for a PDF preview`);
    }

    canvas = createElement('canvas');

    if (!canvas) {
      throw new Error(`Couldn't create canvas for PDF preview`);
    }

    canvas.width = size.width;
    canvas.height = size.height;
    const canvas2d = canvas.getContext('2d');

    if (!canvas2d) {
      throw new Error(`Couldn't create canvas context for PDF preview`);
    }

    await pdf.renderPage(1, canvas2d).promise;

    const blob = await getBlobFromCanvas(canvas);

    return { blob, width: size.width, height: size.height };
  } finally {
    if (canvas) {
      // Work-around for Safari not garbage collecting canvases
      // https://bugs.webkit.org/show_bug.cgi?id=195325
      canvas.width = 0;
    }

    if (pdf) {
      pdf.cleanup();
    }
  }
};

const getBlobFromCanvas = async (canvas: HTMLCanvasElement) => {
  if (canvas.toBlob) {
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error(`Coudn't read rendered canvas`));
        }
      });
    });
  } else {
    // work-around for Edge, get a data url and covert it to a blob
    const url = canvas.toDataURL();
    const parts = url.split(/:|;|,/);
    const type = parts[1];
    const data = atob(parts[3]);

    const buffer = new Uint8Array(data.length);
    for (let i = 0; i < data.length; ++i) {
      buffer[i] = data.charCodeAt(i);
    }

    return new Blob([buffer], { type });
  }
};

export default generatePdfPreview;
